// src/utils/keyvalues-helpers.ts

import type { KeyValueSummary } from '@/types/analytics';

/**
 * Formata números com separadores de milhar
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

/**
 * Formata valores monetários
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Formata percentuais
 */
export const formatPercentage = (value: number, decimals: number = 1): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Formata variações com sinal
 */
export const formatChange = (value: number, decimals: number = 1): string => {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
};

/**
 * Retorna cor baseada no trend
 */
export const getTrendColor = (trend: 'up' | 'down' | 'stable'): string => {
  switch (trend) {
    case 'up':
      return 'text-emerald-600';
    case 'down':
      return 'text-rose-600';
    default:
      return 'text-gray-600';
  }
};

/**
 * Retorna cor de background baseada no trend
 */
export const getTrendBgColor = (trend: 'up' | 'down' | 'stable'): string => {
  switch (trend) {
    case 'up':
      return 'bg-emerald-50';
    case 'down':
      return 'bg-rose-50';
    default:
      return 'bg-gray-50';
  }
};

/**
 * Retorna ícone baseado no trend
 */
export const getTrendIcon = (trend: 'up' | 'down' | 'stable'): string => {
  switch (trend) {
    case 'up':
      return '↑';
    case 'down':
      return '↓';
    default:
      return '→';
  }
};

/**
 * Calcula a diferença percentual entre dois valores
 */
export const calculatePercentageChange = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

/**
 * Extrai o valor total de uma métrica do summary
 */
export const getMetricTotal = (summary: KeyValueSummary, metric: string): number => {
  const metricData = summary[metric as keyof KeyValueSummary];
  return metricData?.total ?? 0;
};

/**
 * Extrai o valor médio de uma métrica do summary
 */
export const getMetricAverage = (summary: KeyValueSummary, metric: string): number => {
  const metricData = summary[metric as keyof KeyValueSummary];
  return metricData?.average ?? 0;
};

/**
 * Cores para gráficos (paleta minimalista)
 */
export const CHART_COLORS = {
  primary: 'rgba(99, 102, 241, 0.8)',      // Indigo
  secondary: 'rgba(139, 92, 246, 0.8)',    // Violet
  tertiary: 'rgba(236, 72, 153, 0.8)',     // Pink
  quaternary: 'rgba(251, 146, 60, 0.8)',   // Orange
  quinary: 'rgba(34, 197, 94, 0.8)',       // Green
  senary: 'rgba(59, 130, 246, 0.8)',       // Blue
  
  // Versões com opacidade para área
  primaryFaded: 'rgba(99, 102, 241, 0.1)',
  secondaryFaded: 'rgba(139, 92, 246, 0.1)',
  tertiaryFaded: 'rgba(236, 72, 153, 0.1)',
  quaternaryFaded: 'rgba(251, 146, 60, 0.1)',
  quinaryFaded: 'rgba(34, 197, 94, 0.1)',
  senaryFaded: 'rgba(59, 130, 246, 0.1)',
};

/**
 * Gera array de cores para múltiplos valores
 */
export const generateColors = (count: number): string[] => {
  const colors = [
    CHART_COLORS.primary,
    CHART_COLORS.secondary,
    CHART_COLORS.tertiary,
    CHART_COLORS.quaternary,
    CHART_COLORS.quinary,
    CHART_COLORS.senary,
  ];
  
  return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

/**
 * Gera array de cores faded para múltiplos valores
 */
export const generateFadedColors = (count: number): string[] => {
  const colors = [
    CHART_COLORS.primaryFaded,
    CHART_COLORS.secondaryFaded,
    CHART_COLORS.tertiaryFaded,
    CHART_COLORS.quaternaryFaded,
    CHART_COLORS.quinaryFaded,
    CHART_COLORS.senaryFaded,
  ];
  
  return Array.from({ length: count }, (_, i) => colors[i % colors.length]);
};

/**
 * Formata data para exibição
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
  }).format(date);
};

/**
 * Trunca texto longo
 */
export const truncateText = (text: string, maxLength: number = 30): string => {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Mapeamento de países para o mapa
 */
export const COUNTRY_MAP: Record<string, string> = {
  'United States': 'United States of America',
  'Brazil': 'Brazil',
  'Mexico': 'Mexico',
  'Chile': 'Chile',
  'Argentina': 'Argentina',
  'Colombia': 'Colombia',
  'Peru': 'Peru',
  'Bolivia': 'Bolivia',
  'Uruguay': 'Uruguay',
  'Paraguay': 'Paraguay',
  'Venezuela': 'Venezuela',
  'Ecuador': 'Ecuador',
  'Poland': 'Poland',
  'Spain': 'Spain',
  'Portugal': 'Portugal',
  'France': 'France',
  'Germany': 'Germany',
  'Italy': 'Italy',
  'United Kingdom': 'United Kingdom',
  'Ireland': 'Ireland',
  'Sweden': 'Sweden',
  'Norway': 'Norway',
  'Denmark': 'Denmark',
  'Finland': 'Finland',
  'Canada': 'Canada',
  'Australia': 'Australia',
  'New Zealand': 'New Zealand',
  'India': 'India',
  'China': 'China',
  'Japan': 'Japan',
  'South Korea': 'South Korea',
  'Vietnam': 'Vietnam',
  'Bangladesh': 'Bangladesh',
  'Philippines': 'Philippines',
  'Indonesia': 'Indonesia',
  'Thailand': 'Thailand',
  'Malaysia': 'Malaysia',
  'Singapore': 'Singapore',
};

/**
 * Obtém label amigável para métrica
 */
export const getMetricLabel = (metric: string): string => {
  const labels: Record<string, string> = {
    revenue: 'Receita',
    impressions: 'Impressões',
    clicks: 'Cliques',
    ecpm: 'eCPM',
    ctr: 'CTR',
    active_view: 'Active View',
    pmr: 'PMR',
    requests_served: 'Requests Served',
    elegible_ad_request: 'Eligible Requests',
    unfilled_impressions: 'Unfilled Impressions',
  };
  
  return labels[metric] || metric;
};

/**
 * Obtém formato adequado para métrica
 */
export const formatMetricValue = (metric: string, value: number): string => {
  switch (metric) {
    case 'revenue':
    case 'ecpm':
      return formatCurrency(value);
    case 'ctr':
    case 'pmr':
    case 'active_view':
      return formatPercentage(value);
    default:
      return formatNumber(value);
  }
};
